import { useState, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Badge,
  Code,
  Group,
  NumberInput,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconExclamationCircleFilled,
  IconThumbDown,
  IconTrash,
} from "@tabler/icons-react";
import TypeTooltip from "./TypeTooltip";
import ConfidenceIcon from "./ConfidenceIcon";
import MeasurementInput from "./Inputs/MeasurementInput";
import ValueListInput from "./Inputs/ValueListInput";
import ValueMultiListInput from "./Inputs/ValueMultiListInput";

function AttribDebug({ attrib, value }) {
  const relevantAttribs = {
    type: attrib.type,
    roles: attrib.roles,
    values: attrib.values,
    valuesList: attrib.valuesList,
    required: attrib.required,
    defaultValue: attrib.defaultValue,
    typeParameters: attrib.typeParameters,
  };

  return (
    <div>
      <Text>{value}</Text>
      <Code block>{JSON.stringify(relevantAttribs, null, 2)}</Code>
    </div>
  );
}

function OriginalValue({ attrib, value }) {
  if (!value) return null;
  switch (attrib.type) {
    case "LIST":
      return (
        <Badge variant="light" size="sm">
          {value}
        </Badge>
      );
    case "LIST_MULTIPLE_VALUES":
      // Value is expected to be an array, but sometimes a single string sneaks through.
      if (Array.isArray(value)) {
        return (
          <Group gap="xs">
            {value.map((v) => (
              <Badge key={v} variant="light" size="sm">
                {v}
              </Badge>
            ))}
          </Group>
        );
      }
      // Gracefully handle non-array values (string / number / etc.)
      if (typeof value === "string" || typeof value === "number") {
        return (
          <Badge variant="light" size="sm">
            {value}
          </Badge>
        );
      }
      // Fallback: stringify unexpected value types.
      return (
        <Badge variant="light" size="sm">
          {String(value)}
        </Badge>
      );
    default:
      return <Text fz="sm">{value}</Text>;
  }
}

function DynamicInputField({ form, attrib, value, error }) {
  const normalizedValue =
    value === null || value === undefined ? "" : value;
  const stringValue =
    typeof normalizedValue === "string"
      ? normalizedValue
      : Array.isArray(normalizedValue)
      ? normalizedValue.join(", ")
      : normalizedValue !== null && normalizedValue !== undefined
      ? String(normalizedValue)
      : "";

  // Memoize onChange handlers to prevent re-creation on every render
  const handleTextChange = useCallback(
    (event) => {
      form.setFieldValue(attrib.code, event.currentTarget.value);
    },
    [form, attrib.code]
  );

  const handleNumberChange = useCallback(
    (val) => {
      form.setFieldValue(attrib.code, val);
    },
    [form, attrib.code]
  );

  switch (attrib.type) {
    case "TEXT":
      return (
        <Textarea
          value={stringValue}
          onChange={handleTextChange}
          error={error}
          multiline
          autosize
          maxRows={3}
        />
      );
    case "LONG_TEXT":
      return (
        <Textarea
          value={stringValue}
          onChange={handleTextChange}
          error={error}
          multiline
          autosize
        />
      );
    case "LIST":
      return (
        <ValueListInput
          form={form}
          attrib={attrib}
          value={typeof normalizedValue === "string" ? normalizedValue : ""}
          error={error}
        />
      );
    case "LIST_MULTIPLE_VALUES":
      return (
        <ValueMultiListInput
          form={form}
          attrib={attrib}
          value={value}
          error={error}
        />
      );
    case "INTEGER":
      return (
        <NumberInput
          value={value}
          onChange={handleNumberChange}
          error={error}
        />
      );
    case "MEASUREMENT":
      return (
        <MeasurementInput
          form={form}
          attrib={attrib}
          value={value}
          error={error}
        />
      );
    default:
      return (
        <TextInput
          value={stringValue}
          onChange={handleTextChange}
          error={error}
        />
      );
  }
}

function ProductAttributeRow({
  form,
  product,
  attrib,
  fieldValue,
  fieldError,
  onDeleteAttribute,
  showOriginalValue,
}) {
  const { t } = useTranslation("pim");
  const [accepted, setAccepted] = useState("");
  const validationFailure = product.validationFailures.find(
    (p) => p.field == attrib.code
  )?.message;
  // const suggestion = product.suggestions
  //   ? product.suggestions[0]?.suggestion.fieldValues[attrib.code] ?? {}
  //   : {};

  // Find the suggestion for the current attribute - this is a bit of a hack:
  const suggestion =
    product.suggestions.find(
      (p) => p?.suggestion.fieldValues[attrib.code]?.reasoning
    )?.suggestion.fieldValues[attrib.code] ?? {};

  // console.log("suggestion found at", suggestion);

  const effectiveFieldValue =
    fieldValue !== undefined && fieldValue !== null
      ? fieldValue
      : product.fieldValues[attrib.code]?.value;

  const currentValue = product.fieldValues[attrib.code]?.value;
  const originalValue =
    product.originalValues?.hasOwnProperty(attrib.code) &&
    product.originalValues[attrib.code];

  const wasChanged = originalValue != currentValue || suggestion.reasoning;

  return (
    <Table.Tr key={attrib.id}>
      <Table.Th>
        <Group gap="xs" wrap="nowrap" justify="space-between">
          <Group gap="sm" wrap="nowrap">
            {validationFailure && (
              <Tooltip
                maw="30%"
                multiline
                label={validationFailure}
                position="top"
                withArrow
              >
                <IconExclamationCircleFilled color="red" />
              </Tooltip>
            )}
            <Tooltip
              maw="40%"
              multiline
              label={<TypeTooltip attrib={attrib} />}
              position="top"
              withArrow
            >
              <div>
                <Text fz="sm">{attrib.label}</Text>
                <Text fz="sm" c="dimmed">
                  {attrib.code}
                </Text>
              </div>
            </Tooltip>
          </Group>
          {attrib.requirementLevel !== "REQUIRED" && (
            <Tooltip
              label={t("legacyProducts.deleteAttributeTooltip")}
              position="top"
              withArrow
            >
              <ActionIcon
                onClick={() => onDeleteAttribute?.(attrib.code)}
                variant="subtle"
              >
                <IconTrash size={14} stroke={1} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Th>
      <Table.Td>
        <DynamicInputField
          form={form}
          attrib={attrib}
          value={effectiveFieldValue}
          error={fieldError}
        />
        {(wasChanged || suggestion.reasoning) && (
          <Group mt={4} wrap="nowrap" align="flex-end" justify="space-between">
            <Text fz="xs" fs="italic" c="dimmed">
              {suggestion.reasoning}
            </Text>
            <Group wrap="nowrap" gap="xs">
              {originalValue && (
                <ActionIcon
                  size="sm"
                  variant={accepted === "rejected" ? "filled" : "default"}
                  onClick={() => {
                    setAccepted(accepted === "rejected" ? "" : "rejected");
                    form.setFieldValue(attrib.code, originalValue);
                  }}
                >
                  <IconThumbDown size={14} stroke={1} />
                </ActionIcon>
              )}
              <ConfidenceIcon confidence={suggestion.confidence} />
            </Group>
          </Group>
        )}
      </Table.Td>
      {showOriginalValue && (
        <Table.Td>
          <OriginalValue attrib={attrib} value={originalValue} />
        </Table.Td>
      )}
    </Table.Tr>
  );
}

function propsAreEqual(prev, next) {
  return (
    prev.fieldValue === next.fieldValue &&
    prev.fieldError === next.fieldError &&
    prev.showOriginalValue === next.showOriginalValue &&
    prev.attrib === next.attrib &&
    prev.product === next.product
  );
}

export default memo(ProductAttributeRow, propsAreEqual);
